import { Badge, Box, Center, Divider, Flex, Skeleton, Stack, Text } from "@mantine/core";
import { IconBuilding, IconSparkles } from "@tabler/icons-react";
import { useLocation, useNavigate } from "react-router-dom";
import { type Dispatch, type SetStateAction } from "react";
import { type CompanyWithUI } from "@/components/shared/CompanyCardHorizontal";
import DiscoverCompaniesTable from "@/pages/discover/components/DiscoverCompaniesTable";
import InfoBanner from "@/components/shared/InfoBanner";
import AppPaginationBar from "@/components/shared/AppPaginationBar";
import { MultiStepLoader } from "@/components/shared/MultiStepLoader";
import { successNotification } from "@/shared/utils/successNotification";
import { handleTrpcError } from "@/shared/utils/trpc/handletrpcError.util";
import { addGuestShortlistCompanyId } from "@/shared/utils/guestShortlistStorage";
import { navigateToCompanyWorkspace } from "@/shared/utils/collaborationNavigation.util";

type DiscoverResultsSectionProps = {
  companies: CompanyWithUI[];
  selectedSectorTitle: string | null;
  debouncedSearch: string;
  isAuthenticated: boolean;
  isAiSearch: boolean;
  loading: boolean;
  effectiveLoading: boolean;
  showResultsShimmer: boolean;
  matchCount: number | null;
  loadError: string | null;
  isHomeRoute: boolean;
  totalPages: number;
  page: number;
  effectivePageSize: number;
  pageSizeOptions: readonly number[];
  onPageChange: (nextPage: number) => void;
  onPageSizeChange?: (nextPageSize: number) => void;
  addToShortlist: (companyId: string) => Promise<void>;
  removeFromShortlist: (companyId: string) => Promise<void>;
  setCompanies: Dispatch<SetStateAction<CompanyWithUI[]>>;
  setGuestShortlistedIds: Dispatch<SetStateAction<Set<string>>>;
  buildDiscoverProfileSearch: () => string;
  isMyDiscoveryRoute: boolean;
  canViewCompanyDetails?: boolean;
  onBlockedViewCompanyDetails?: () => void;
};

export const DiscoverResultsSection = ({
  companies,
  selectedSectorTitle,
  debouncedSearch,
  isAuthenticated,
  isAiSearch,
  loading,
  effectiveLoading,
  showResultsShimmer,
  matchCount,
  loadError,
  isHomeRoute,
  totalPages,
  page,
  effectivePageSize,
  pageSizeOptions,
  onPageChange,
  onPageSizeChange,
  addToShortlist,
  removeFromShortlist,
  setCompanies,
  setGuestShortlistedIds,
  buildDiscoverProfileSearch,
  isMyDiscoveryRoute: _isMyDiscoveryRoute,
  canViewCompanyDetails = true,
  onBlockedViewCompanyDetails,
}: DiscoverResultsSectionProps) => {
  const navigate = useNavigate();
  const location = useLocation();

  return (
    <>
      <Box>
        <Flex justify="space-between" align="center" mb="xs">
          <Text fw={700} c={!debouncedSearch.trim() ? "primary.7" : "slate.6"}>
            {debouncedSearch.trim() ? "All Companies" : (selectedSectorTitle ?? "All Companies")}
          </Text>
          <Text fw={700} c={debouncedSearch.trim() ? "primary.7" : "slate.6"}>
            Search Results
          </Text>
        </Flex>
        <Divider color="slate.2" />
      </Box>

      {!isAuthenticated ? (
        <InfoBanner
          icon={IconSparkles}
          title="Sign up to unlock more"
          subtitle="Create an account to express interest, shortlist companies, and access full profiles."
        />
      ) : null}

      {isAiSearch ? (
        <Flex align="center" justify="space-between" wrap="wrap" gap="sm" mt="lg" mb="lg" w="100%">
          <Badge
            leftSection={<IconSparkles size={12} stroke={1.5} color="var(--mantine-color-violet-6)" />}
            color="violet"
            variant="light"
            size="sm"
            styles={{
              root: { textTransform: "uppercase", letterSpacing: "0.05em" },
              label: { fontWeight: 700, fontSize: 12 },
            }}
          >
            AI Search
          </Badge>
          <Badge
            leftSection={<IconBuilding size={12} stroke={1.5} color="var(--mantine-color-violet-6)" />}
            color="violet"
            variant="light"
            size="sm"
            styles={{
              root: { textTransform: "none" },
              label: { fontWeight: 700, fontSize: 12, letterSpacing: "0.02em" },
            }}
          >
            {loading && matchCount === null
              ? "..."
              : `${(matchCount ?? 0).toLocaleString()} ${matchCount === 1 ? "company" : "companies"}`}
          </Badge>
        </Flex>
      ) : effectiveLoading ? (
        <Text c="slate.6" size="sm" mb="md">
          Searching...
        </Text>
      ) : null}

      <Box style={{ position: "relative", minHeight: 200 }} mt={isAiSearch ? "md" : "lg"}>
        <MultiStepLoader
          loading={effectiveLoading}
          inline
          minimal
          loadingStates={[
            { text: "Searching companies..." },
            { text: "Analyzing your query..." },
            { text: "Finding best matches..." },
            { text: "Ranking results..." },
          ]}
          title=""
          subtitle=""
          textSize="lg"
          duration={1500}
        />

        {showResultsShimmer ? (
          <Box
            p="md"
            style={{
              border: "1px solid #e5e7eb",
              borderRadius: 12,
              backgroundColor: "#fff",
            }}
          >
            <Stack gap={0}>
              <Flex gap="md" pb="sm" style={{ borderBottom: "1px solid #e5e7eb" }}>
                {[
                  { flex: 1.1, key: "company" },
                  { flex: 2.2, key: "description" },
                  { flex: 1, key: "industry" },
                  { flex: 0.95, key: "location" },
                  { flex: 1, key: "tags" },
                  { flex: 0, w: 160, key: "actions" },
                ].map((col) => (
                  <Skeleton
                    key={`discover-shimmer-h-${col.key}`}
                    height={14}
                    radius="sm"
                    style={{ flex: col.flex > 0 ? col.flex : undefined, width: col.w }}
                  />
                ))}
              </Flex>
              {Array.from({ length: 6 }).map((_, rowIdx) => (
                <Flex
                  key={`discover-shimmer-r-${rowIdx}`}
                  gap="md"
                  py="sm"
                  align="center"
                  style={{ borderBottom: rowIdx < 5 ? "1px solid #f1f5f9" : undefined }}
                >
                  <Skeleton height={16} radius="sm" style={{ flex: 1.1 }} />
                  <Skeleton height={16} radius="sm" style={{ flex: 2.2 }} />
                  <Skeleton height={16} radius="sm" style={{ flex: 1 }} />
                  <Skeleton height={16} radius="sm" style={{ flex: 0.95 }} />
                  <Skeleton height={16} radius="sm" style={{ flex: 1 }} />
                  <Skeleton height={32} radius="md" w={160} />
                </Flex>
              ))}
            </Stack>
          </Box>
        ) : isAiSearch && loading ? null : companies.length === 0 && !loading ? (
          <Center py="xl">
            <Text c="slate.5"> No results found for your search. Please try again with different filters or keywords.</Text>
          </Center>
        ) : (
          <DiscoverCompaniesTable
            companies={companies}
            profileSearch={buildDiscoverProfileSearch()}
            discoverReturnTo={(() => {
              const qs = buildDiscoverProfileSearch();
              return qs ? `${location.pathname}${qs}` : location.pathname;
            })()}
            canViewCompanyDetails={canViewCompanyDetails}
            onBlockedViewCompanyDetails={onBlockedViewCompanyDetails}
            onShortlistClick={async (id, isShortlisted) => {
              let previous = false;
              setCompanies((prev) =>
                prev.map((c) => {
                  if (c.id !== id) return c;
                  previous = Boolean(c.isShortlisted);
                  return { ...c, isShortlisted };
                }),
              );

              try {
                if (isShortlisted) {
                  await addToShortlist(id);
                  successNotification({ title: "Success", message: "Company added to shortlist" });
                } else {
                  await removeFromShortlist(id);
                  successNotification({ title: "Success", message: "Company removed from shortlist" });
                }
              } catch (err) {
                handleTrpcError(err);
                setCompanies((prev) => prev.map((c) => (c.id === id ? { ...c, isShortlisted: previous } : c)));
              }
            }}
            onGuestShortlistClick={(id) => {
              const nextIds = addGuestShortlistCompanyId(id);
              const nextSet = new Set(nextIds);
              setGuestShortlistedIds(nextSet);
              setCompanies((prev) => prev.map((c) => (c.id === id ? { ...c, isShortlisted: true } : c)));
            }}
            onViewDetails={(id) => {
              if (!canViewCompanyDetails) {
                onBlockedViewCompanyDetails?.();
                return;
              }
              const qs = buildDiscoverProfileSearch();
              const returnTo = qs ? `${location.pathname}${qs}` : location.pathname;
              if (isAuthenticated) {
                void navigateToCompanyWorkspace(navigate, id, { state: { discoverReturnTo: returnTo } });
                return;
              }
              navigate(`/discover/${id}${qs}`, { state: { discoverReturnTo: returnTo } });
            }}
          />
        )}
      </Box>

      {loadError && (
        <Center py="md">
          <Text c="red" size="sm">
            {loadError}
          </Text>
        </Center>
      )}

      {(!isHomeRoute || totalPages > 1) && !isAiSearch && (
        <Box mt="xl">
          <AppPaginationBar
            page={page}
            totalPages={totalPages}
            totalItems={matchCount ?? companies.length}
            pageSize={effectivePageSize}
            onPageChange={onPageChange}
            onPageSizeChange={onPageSizeChange}
            pageSizeOptions={[...pageSizeOptions]}
            boundaries={1}
          />
        </Box>
      )}
    </>
  );
};
